// TaggedInput.js
import React from 'react';
import { useField } from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { DropdownOption } from 'types/common/DropdownOption';
import {
  Box,
  Chip,
  FormHelperText,
  Grid,
  GridProps,
  InputLabel,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSchemaContext } from './SparkForm';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SparkImageLoader } from 'sharedComponents/UI';

type SparkFormTaggedAutoCompleteProps = {
  name: string;
  label: string;
  options: DropdownOption[];
  placeholder?: string;
  size?: GridProps['xs'];
  helpText?: string;
  sx?: TextFieldProps['sx'];
  maxSelections?: number;
  disabled?: boolean;
};

const SparkFormTaggedAutoComplete = ({
  name,
  label,
  placeholder,
  size = 12,
  helpText,
  sx = {},
  options,
  maxSelections,
  disabled = false,
}: SparkFormTaggedAutoCompleteProps) => {
  const [field, meta, helpers] = useField<DropdownOption['value'][]>(name);
  const { validationSchema } = useSchemaContext();
  const isError = meta.touched && !!meta.error;

  const handleChange = (selectedOptions: DropdownOption[]) => {
    if (!!maxSelections && selectedOptions.length > maxSelections) {
      return;
    }
    helpers.setValue(selectedOptions.map((option) => option.value));
  };
  const filteredOptions = options.filter(
    (option) => !field.value.includes(option.value)
  );
  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  return (
    <Grid item xs={size} container>
      <InputLabel
        sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
      >
        <span>{isFieldRequired && '*'}</span>
        {label}
        {helpText && (
          <Tooltip title={helpText} placement='top' arrow>
            <InfoOutlinedIcon fontSize='small' />
          </Tooltip>
        )}
      </InputLabel>
      <Autocomplete
        value={options.filter((option) =>
          field.value?.some((value) => option.value === value)
        )}
        multiple
        limitTags={maxSelections}
        id={name}
        fullWidth
        options={filteredOptions}
        getOptionLabel={(option) => option.label}
        onChange={(_, values) => handleChange(values)}
        disabled={disabled}
        renderOption={(props, option) => (
          <MenuItem value={option.value} dense divider {...props}>
            <Box display='flex' flexDirection='column' p={1}>
              <Box display='flex' alignItems='center' gap={2}>
                {!!option.iconUrl && (
                  <SparkImageLoader
                    style={{ width: 24, aspectRatio: 2 / 1 }}
                    src={option.iconUrl}
                    alt={option.iconUrl}
                    loading='lazy'
                  />
                )}
                <Typography fontFamily='Mont-semibold' fontSize={14}>
                  {option.label}
                </Typography>
              </Box>
              {!!option.description && (
                <Typography
                  fontSize={14}
                  sx={{
                    textWrap: 'wrap',
                    color: '#4A4A4A',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    lineHeight: 'normal',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                >
                  {option.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              sx={{
                borderRadius: '10px',
                bgcolor: (theme) => theme.palette.primary.light,
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
              }}
              label={option.label}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={''}
            placeholder={placeholder || label || ''}
            fullWidth
            error={isError}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              ...sx,
            }}
            {...field}
          />
        )}
      />
      <FormHelperText
        sx={{
          color: (theme) => theme.palette.error.main,
          marginLeft: 1,
          marginBottom: 0.5,
        }}
      >
        {isError ? meta.error : ' '}
      </FormHelperText>
    </Grid>
  );
};

export default SparkFormTaggedAutoComplete;
